import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { GET_PRODUCTS } from '../../graphql/product-queries'
import { CALCULATE_COGS, GET_VARIANT_OPTIONS_FOR_MATRIX } from '../../graphql/costing-queries'
import { CogsResult } from '../../types/costing'
import { formatCost } from '../../lib/costingUtils'
import LoadingSpinner from '../../components/LoadingSpinner'

const PAGE_SIZE = 25

/* ─── Types ─────────────────────────────────────────────────────────────────── */

interface PlanningItem {
  id: number
  label: string
  price_delta_cents: number
  option_group_id: number
  option_group_name: string
}

interface PlanningGroup {
  groupId: number
  groupName: string
  items: PlanningItem[]
  minSelect: number
  maxSelect: number
  /** True when the group also holds non-planning items (a free/default choice the guest could pick instead). */
  hasOtherItems: boolean
}

interface Combination {
  items: PlanningItem[]
  selectedIds: number[]
  priceDeltaCents: number
}

/* ─── Helpers ───────────────────────────────────────────────────────────────── */

/** All k-sized combinations (order-independent) of arr. */
function combinationsOfSize(arr: PlanningItem[], k: number): PlanningItem[][] {
  if (k === 0) return [[]]
  if (k > arr.length) return []
  const result: PlanningItem[][] = []
  const rec = (start: number, combo: PlanningItem[]) => {
    if (combo.length === k) {
      result.push(combo.slice())
      return
    }
    for (let i = start; i < arr.length; i++) {
      combo.push(arr[i])
      rec(i + 1, combo)
      combo.pop()
    }
  }
  rec(0, [])
  return result
}

/**
 * The valid selection-sets for a single group, honouring its cardinality.
 * The "select nothing" branch is included when the group is optional (min_select 0)
 * or has a non-planning default the guest could pick instead — so an optional
 * add-on like "Make it spicier" no longer gets force-added to every row.
 */
function groupSelections(group: PlanningGroup): PlanningItem[][] {
  const hi = Math.max(1, Math.min(group.maxSelect, group.items.length))
  const lo = group.minSelect === 0 || group.hasOtherItems ? 0 : 1
  const selections: PlanningItem[][] = []
  for (let k = lo; k <= hi; k++) {
    for (const combo of combinationsOfSize(group.items, k)) selections.push(combo)
  }
  return selections.length > 0 ? selections : [[]]
}

function buildCombinations(planningGroups: PlanningGroup[]): Combination[] {
  const base: Combination = { items: [], selectedIds: [], priceDeltaCents: 0 }
  if (planningGroups.length === 0) return [base]

  // Cartesian product across groups, concatenating one valid selection from each.
  let combos: PlanningItem[][] = [[]]
  for (const group of planningGroups) {
    const selections = groupSelections(group)
    const next: PlanningItem[][] = []
    for (const combo of combos) {
      for (const selection of selections) next.push([...combo, ...selection])
    }
    combos = next
  }

  return combos.map((items) => ({
    items,
    selectedIds: items.map((i) => i.id),
    priceDeltaCents: items.reduce((sum, i) => sum + i.price_delta_cents, 0),
  }))
}

/* ─── Single matrix row ─────────────────────────────────────────────────────── */

interface MatrixRowProps {
  variantId: number
  combination: Combination
  variantPriceCents: number
  isFirst: boolean
  variantName: string
  productName: string
  productId: number
}

const MatrixRow = ({
  variantId,
  combination,
  variantPriceCents,
  isFirst,
  variantName,
  productName,
  productId,
}: MatrixRowProps) => {
  const { data, loading } = useQuery(CALCULATE_COGS, {
    variables: { variantId, selectedOptionItemIds: combination.selectedIds },
    fetchPolicy: 'cache-first',
  })

  const cogs: CogsResult | null = data?.calculateCogs ?? null
  const sellPriceCents = variantPriceCents + combination.priceDeltaCents
  const sellPriceMillicents = sellPriceCents * 1000

  const marginPct =
    cogs && cogs.total_cogs_millicents > 0 && sellPriceCents > 0
      ? ((sellPriceMillicents - cogs.total_cogs_millicents) / sellPriceMillicents) * 100
      : null

  const marginColor =
    marginPct === null
      ? 'text-gray-400'
      : marginPct < 20
      ? 'text-red-600'
      : marginPct < 50
      ? 'text-amber-600'
      : 'text-green-600'

  const optionLabel =
    combination.items.length === 0
      ? <span className="italic text-gray-400">base</span>
      : combination.items.map((i) => (
          <span key={i.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium mr-1">
            +{i.label}
          </span>
        ))

  return (
    <tr className="hover:bg-gray-50">
      {isFirst ? (
        <>
          <td className="px-6 py-3 text-sm text-gray-500 whitespace-nowrap">{productName}</td>
          <td className="px-6 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
            <Link
              to={`/products/${productId}/variants/${variantId}?tab=recipe`}
              className="hover:text-primary-600 hover:underline"
            >
              {variantName}
            </Link>
          </td>
        </>
      ) : (
        <>
          <td className="px-6 py-3" />
          <td className="px-6 py-3" />
        </>
      )}
      <td className="px-6 py-3 text-sm text-gray-700">{optionLabel}</td>
      <td className="px-6 py-3 text-sm text-gray-700 whitespace-nowrap">
        €{(sellPriceCents / 100).toFixed(2)}
      </td>
      <td className="px-6 py-3 text-sm whitespace-nowrap">
        {loading ? (
          <span className="text-gray-400 text-xs">Loading…</span>
        ) : cogs === null ? (
          <span className="text-gray-400">—</span>
        ) : (
          <span className="text-gray-900">{formatCost(cogs.variant_cogs_millicents)}</span>
        )}
      </td>
      <td className="px-6 py-3 text-sm whitespace-nowrap">
        {loading ? (
          <span className="text-gray-400 text-xs">Loading…</span>
        ) : cogs === null ? (
          <span className="text-gray-400">—</span>
        ) : (
          <span className="text-gray-900">{formatCost(cogs.option_cogs_millicents)}</span>
        )}
      </td>
      <td className="px-6 py-3 text-sm whitespace-nowrap">
        {loading ? (
          <span className="text-gray-400 text-xs">Loading…</span>
        ) : cogs === null ? (
          <span className="text-gray-400">—</span>
        ) : (
          <span className="text-gray-900 font-medium">{formatCost(cogs.total_cogs_millicents)}</span>
        )}
      </td>
      <td className={`px-6 py-3 text-sm font-medium whitespace-nowrap ${marginColor}`}>
        {loading ? (
          <span className="text-gray-400 text-xs">—</span>
        ) : marginPct !== null ? (
          `${marginPct.toFixed(1)}%`
        ) : (
          '—'
        )}
      </td>
    </tr>
  )
}

/* ─── Variant section ───────────────────────────────────────────────────────── */

interface VariantMatrixSectionProps {
  variantId: number
  variantName: string
  productName: string
  priceCents: number
  productId: number
}

const VariantMatrixSection = ({
  variantId,
  variantName,
  productName,
  priceCents,
  productId,
}: VariantMatrixSectionProps) => {
  const { data } = useQuery(GET_VARIANT_OPTIONS_FOR_MATRIX, {
    variables: { variantId },
    fetchPolicy: 'cache-first',
  })

  const optionGroupLinks: any[] = data?.variantOptionGroupsByVariant ?? []

  const planningGroups: PlanningGroup[] = optionGroupLinks
    .map((link: any): PlanningGroup => {
      const allItems = link.option_items ?? []
      const items: PlanningItem[] = allItems
        .filter((item: any) => item.planning_relevant)
        .map((item: any) => ({
          id: item.id,
          label: item.label,
          price_delta_cents: item.price_delta_cents,
          option_group_id: link.option_group_id,
          option_group_name: link.name,
        }))
      return {
        groupId: link.option_group_id,
        groupName: link.name,
        items,
        minSelect: link.min_select ?? 0,
        maxSelect: link.max_select ?? 1,
        hasOtherItems: allItems.length > items.length,
      }
    })
    .filter((g) => g.items.length > 0)

  const combinations = buildCombinations(planningGroups)

  return (
    <>
      {combinations.map((combo, idx) => (
        <MatrixRow
          key={`${variantId}-${combo.selectedIds.join('-') || 'base'}`}
          variantId={variantId}
          combination={combo}
          variantPriceCents={priceCents}
          isFirst={idx === 0}
          variantName={variantName}
          productName={productName}
          productId={productId}
        />
      ))}
      <tr className="h-1 bg-gray-100">
        <td colSpan={8} />
      </tr>
    </>
  )
}

/* ─── Page ──────────────────────────────────────────────────────────────────── */

const VariantCostsPage = () => {
  const [search, setSearch] = useState('')
  const [activeOnly, setActiveOnly] = useState(true)
  const [page, setPage] = useState(0)

  const { data, loading, error } = useQuery(GET_PRODUCTS, {
    variables: { filters: { active: activeOnly || undefined } },
  })

  const products: any[] = data?.products ?? []

  const allVariants = products.flatMap((p: any) =>
    p.variants
      .filter((v: any) => !activeOnly || v.active)
      .map((v: any) => ({ ...v, productName: p.name, productId: p.id }))
  )

  const filtered = search.trim()
    ? allVariants.filter(
        (v) =>
          v.name.toLowerCase().includes(search.toLowerCase()) ||
          v.productName.toLowerCase().includes(search.toLowerCase())
      )
    : allVariants

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const safePage = Math.min(page, Math.max(0, totalPages - 1))
  const pageVariants = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE)

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(0)
  }

  const handleActiveOnly = (value: boolean) => {
    setActiveOnly(value)
    setPage(0)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Margin Planning Matrix</h1>
        <p className="mt-1 text-sm text-gray-500">
          COGS and margin per variant, expanded for every planning-relevant option combination.
          Mark option choices as planning-relevant on the option item detail page.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by variant or product name…"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="input pl-9 w-full"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer self-center">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => handleActiveOnly(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600"
          />
          Active variants only
        </label>
      </div>

      {!loading && filtered.length > 0 && (
        <p className="text-sm text-gray-500">
          Showing <span className="font-medium">{safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, filtered.length)}</span> of{' '}
          <span className="font-medium">{filtered.length}</span> variant{filtered.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-red-600">{error.message}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center">
          <p className="text-sm text-gray-500">
            {search ? 'No variants match your search.' : 'No variants found.'}
          </p>
        </div>
      ) : (
        <>
          <div className="card overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Variant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Options
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sell Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Base Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Option Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Margin
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pageVariants.map((v) => (
                  <VariantMatrixSection
                    key={v.id}
                    variantId={v.id}
                    variantName={v.name}
                    productName={v.productName}
                    productId={v.productId}
                    priceCents={v.price_cents}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
                className="btn-secondary flex items-center gap-1 disabled:opacity-40"
              >
                <ChevronLeftIcon className="h-4 w-4" />
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {safePage + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={safePage === totalPages - 1}
                className="btn-secondary flex items-center gap-1 disabled:opacity-40"
              >
                Next
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default VariantCostsPage
